import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Import types from the API
import { ExperimentDesignFull, AdditionalSample, buildHierarchicalDesigns } from '../../lib/api/experimentDesignApi';
import { ensureSampleSizeConsistency } from '../../app/scheduling/experiment_design/utils';

interface ExperimentDesignState {
    designs: ExperimentDesignFull[];
    hierarchicalDesigns: ExperimentDesignFull[]; // Organized in parent-child structure
    activeDesignId: number | null;
    isLoading: boolean;
    error: string | null;
    // Push to queue state
    isPushingToQueue: boolean;
    pushToQueueError: string | null;
    // Queue tracking - maps design ID to count of experiments in queue
    queuedExperimentsCounts: Record<number, number>;
}

const initialState: ExperimentDesignState = {
    designs: [],
    hierarchicalDesigns: [],
    activeDesignId: null,
    isLoading: false,
    error: null,
    isPushingToQueue: false,
    pushToQueueError: null,
    queuedExperimentsCounts: {},
};

const experimentDesignSlice = createSlice({
    name: 'experimentDesign',
    initialState,
    reducers: {
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },
        setError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
        },
        setDesigns: (state, action: PayloadAction<ExperimentDesignFull[]>) => {
            // Ensure designs follow sample_size consistency rules
            const designs = ensureSampleSizeConsistency(action.payload);
            state.designs = designs;
            state.hierarchicalDesigns = buildHierarchicalDesigns(designs) || [];
            state.error = null;
        },
        addDesign: (state, action: PayloadAction<ExperimentDesignFull>) => {
            const newDesign = action.payload;
            // If the design has children, ensure its sample_size is null
            if (newDesign.children && newDesign.children.length > 0) {
                newDesign.sample_size = null;
            }
            state.designs.push(newDesign);
            state.hierarchicalDesigns = buildHierarchicalDesigns(state.designs) || [];
        },
        updateDesign: (state, action: PayloadAction<ExperimentDesignFull>) => {
            const index = state.designs.findIndex(design => design.experiment_design_id === action.payload.experiment_design_id);
            if (index !== -1) {
                state.designs[index] = action.payload;
                state.hierarchicalDesigns = buildHierarchicalDesigns(state.designs) || [];
            }
        },
        removeDesign: (state, action: PayloadAction<number>) => {
            // Remove the design and all its descendants
            const designToRemove = state.designs.find(design => design.experiment_design_id === action.payload);
            if (designToRemove) {
                const parentId = designToRemove.parent_experiment_design_id;
                const idsToRemove = [action.payload];

                // Find all descendants recursively
                const findDescendants = (parentId: number) => {
                    const children = state.designs.filter(design => design.parent_experiment_design_id === parentId);
                    children.forEach(child => {
                        idsToRemove.push(child.experiment_design_id);
                        findDescendants(child.experiment_design_id);
                    });
                };
                findDescendants(action.payload);

                // Remove all designs in the hierarchy
                state.designs = state.designs.filter(design => !idsToRemove.includes(design.experiment_design_id));

                // Check if the parent of the removed design now has no children
                if (parentId !== null) {
                    const parent = state.designs.find(d => d.experiment_design_id === parentId);
                    const parentStillHasChildren = state.designs.some(d => d.parent_experiment_design_id === parentId);
                    if (parent && !parentStillHasChildren) {
                        // Parent is now a leaf node, it can have a sample_size again (set to null initially)
                        parent.sample_size = null;
                    }
                }

                state.hierarchicalDesigns = buildHierarchicalDesigns(state.designs) || [];

                // Clear active design if it was deleted
                if (idsToRemove.includes(state.activeDesignId!)) {
                    state.activeDesignId = null;
                }
            }
        },
        setActiveDesign: (state, action: PayloadAction<number | null>) => {
            state.activeDesignId = action.payload;
        },
        clearDesigns: (state) => {
            state.designs = [];
            state.hierarchicalDesigns = [];
            state.activeDesignId = null;
            state.error = null;
        },
        // New actions for hierarchical management
        addChildDesign: (state, action: PayloadAction<{ parentId: number; child: ExperimentDesignFull }>) => {
            const { parentId, child } = action.payload;
            // Ensure parent_experiment_design_id is set correctly
            const childWithParent = { ...child, parent_experiment_design_id: parentId };
            state.designs.push(childWithParent);

            // Find the parent design and set its sample_size to null since it now has children
            const parentDesign = state.designs.find(d => d.experiment_design_id === parentId);
            if (parentDesign) {
                parentDesign.sample_size = null;
            }

            state.hierarchicalDesigns = buildHierarchicalDesigns(state.designs) || [];
        },
        moveDesignToParent: (state, action: PayloadAction<{ designId: number; newParentId: number | null }>) => {
            const { designId, newParentId } = action.payload;
            const design = state.designs.find(d => d.experiment_design_id === designId);
            if (design) {
                const oldParentId = design.parent_experiment_design_id;
                design.parent_experiment_design_id = newParentId;

                // Check if old parent now has no children and should get its sample_size back
                if (oldParentId !== null) {
                    const oldParent = state.designs.find(d => d.experiment_design_id === oldParentId);
                    const oldParentStillHasChildren = state.designs.some(d => d.parent_experiment_design_id === oldParentId && d.experiment_design_id !== designId);
                    if (oldParent && !oldParentStillHasChildren) {
                        // Old parent is now a leaf, it can have a sample_size again (but keep null for now)
                        oldParent.sample_size = null;
                    }
                }

                // Check if new parent now has children and should have null sample_size
                if (newParentId !== null) {
                    const newParent = state.designs.find(d => d.experiment_design_id === newParentId);
                    if (newParent) {
                        newParent.sample_size = null;
                    }
                }

                state.hierarchicalDesigns = buildHierarchicalDesigns(state.designs) || [];
            }
        },
        // Push to queue actions
        setPushingToQueue: (state, action: PayloadAction<boolean>) => {
            state.isPushingToQueue = action.payload;
        },
        setPushToQueueError: (state, action: PayloadAction<string | null>) => {
            state.pushToQueueError = action.payload;
        },
        // Queue tracking actions
        setQueuedExperimentsCounts: (state, action: PayloadAction<Record<number, number>>) => {
            state.queuedExperimentsCounts = action.payload;
        },
        // Add a design to the front of the list (used for creating)
        prependDesign: (state, action: PayloadAction<ExperimentDesignFull>) => {
            const newDesign = action.payload;
            // If the design has children, ensure its sample_size is null
            if (newDesign.children && newDesign.children.length > 0) {
                newDesign.sample_size = null;
            }
            state.designs.unshift(newDesign); // Add to front
            state.hierarchicalDesigns = buildHierarchicalDesigns(state.designs) || [];
        },
        // Add a design by parent (used for creating children)
        addDesignByParent: (state, action: PayloadAction<{ design: ExperimentDesignFull; parentId: number | null }>) => {
            const { design, parentId } = action.payload;
            const designWithParent = { ...design, parent_experiment_design_id: parentId };
            state.designs.push(designWithParent);

            // If it has a parent, ensure the parent's sample_size is null
            if (parentId !== null) {
                const parentDesign = state.designs.find(d => d.experiment_design_id === parentId);
                if (parentDesign) {
                    parentDesign.sample_size = null;
                }
            }

            state.hierarchicalDesigns = buildHierarchicalDesigns(state.designs) || [];
        },
        // Replace a design in place (used for updating)
        replaceDesign: (state, action: PayloadAction<ExperimentDesignFull>) => {
            const updatedDesign = action.payload;
            const index = state.designs.findIndex(d => d.experiment_design_id === updatedDesign.experiment_design_id);
            if (index !== -1) {
                state.designs[index] = updatedDesign;
                state.hierarchicalDesigns = buildHierarchicalDesigns(state.designs) || [];
            }
        },
        // Additional samples actions
        updateAdditionalSamplesRequired: (state, action: PayloadAction<{ designId: number; required: boolean }>) => {
            const { designId, required } = action.payload;
            const design = state.designs.find(d => d.experiment_design_id === designId);
            if (design) {
                design.additional_samples_required = required;
                if (!required) {
                    // Clear additional samples when disabling
                    design.additional_samples = [];
                }
                state.hierarchicalDesigns = buildHierarchicalDesigns(state.designs) || [];
            }
        },
        updateAdditionalSamples: (state, action: PayloadAction<{ designId: number; samples: AdditionalSample[] }>) => {
            const { designId, samples } = action.payload;
            const design = state.designs.find(d => d.experiment_design_id === designId);
            if (design) {
                design.additional_samples = samples;
                state.hierarchicalDesigns = buildHierarchicalDesigns(state.designs) || [];
            }
        },
        addMultipleDesigns: (state, action: PayloadAction<ExperimentDesignFull[]>) => {
            const addDesignAndChildren = (design: ExperimentDesignFull) => {
                // If the design has children, ensure its sample_size is null
                if (design.children && design.children.length > 0) {
                    design.sample_size = null;
                    const children = [...design.children];
                    delete design.children; // remove children from the design to avoid nested objects in the flat list
                    state.designs.push(design);
                    children.forEach(child => addDesignAndChildren(child));
                } else {
                    state.designs.push(design);
                }
            };

            action.payload.forEach(newDesign => {
                addDesignAndChildren(newDesign);
            });

            state.hierarchicalDesigns = buildHierarchicalDesigns(state.designs) || [];
        },
    },
});

// Export actions
export const {
    setLoading,
    setError,
    setDesigns,
    addDesign,
    updateDesign,
    removeDesign,
    setActiveDesign,
    clearDesigns,
    addChildDesign,
    moveDesignToParent,
    setPushingToQueue,
    setPushToQueueError,
    setQueuedExperimentsCounts,
    prependDesign,
    addDesignByParent,
    replaceDesign,
    updateAdditionalSamplesRequired,
    updateAdditionalSamples,
    addMultipleDesigns,
} = experimentDesignSlice.actions;

// Selectors
export const selectAllDesigns = (state: { experimentDesign: ExperimentDesignState }) =>
    state.experimentDesign.designs;

export const selectHierarchicalDesigns = (state: { experimentDesign: ExperimentDesignState }) =>
    state.experimentDesign.hierarchicalDesigns;

const selectRootDesigns = (state: { experimentDesign: ExperimentDesignState }) =>
    state.experimentDesign.designs.filter(design => design.parent_experiment_design_id === null || design.parent_experiment_design_id === undefined);

const selectChildrenOfDesign = (state: { experimentDesign: ExperimentDesignState }, parentId: number) =>
    state.experimentDesign.designs.filter(design => design.parent_experiment_design_id === parentId);

const selectActiveDesignId = (state: { experimentDesign: ExperimentDesignState }) =>
    state.experimentDesign.activeDesignId;

const selectActiveDesign = (state: { experimentDesign: ExperimentDesignState }) => {
    if (!state.experimentDesign.activeDesignId) return null;
    return state.experimentDesign.designs.find(
        design => design.experiment_design_id === state.experimentDesign.activeDesignId
    ) || null;
};

const selectDesignById = (state: { experimentDesign: ExperimentDesignState }, id: number) =>
    state.experimentDesign.designs.find(design => design.experiment_design_id === id);

export const selectDesignsLoading = (state: { experimentDesign: ExperimentDesignState }) =>
    state.experimentDesign.isLoading;

export const selectDesignsError = (state: { experimentDesign: ExperimentDesignState }) =>
    state.experimentDesign.error;

const selectDesignsCount = (state: { experimentDesign: ExperimentDesignState }) =>
    state.experimentDesign.designs.length;

export const selectRootDesignsCount = (state: { experimentDesign: ExperimentDesignState }) =>
    state.experimentDesign.designs.filter(design => design.parent_experiment_design_id === null || design.parent_experiment_design_id === undefined).length;

const selectDesignWithChildren = (state: { experimentDesign: ExperimentDesignState }, id: number) => {
    const findInHierarchy = (designs: ExperimentDesignFull[]): ExperimentDesignFull | null => {
        for (const design of designs) {
            if (design.experiment_design_id === id) return design;
            if (design.children) {
                const found = findInHierarchy(design.children);
                if (found) return found;
            }
        }
        return null;
    };
    return findInHierarchy(state.experimentDesign.hierarchicalDesigns);
};

// Push to queue selectors
export const selectIsPushingToQueue = (state: { experimentDesign: ExperimentDesignState }) =>
    state.experimentDesign.isPushingToQueue;

export const selectPushToQueueError = (state: { experimentDesign: ExperimentDesignState }) =>
    state.experimentDesign.pushToQueueError;

// Queue tracking selectors
export const selectQueuedExperimentsCounts = (state: { experimentDesign: ExperimentDesignState }) =>
    state.experimentDesign.queuedExperimentsCounts;

const selectQueuedExperimentsCountForDesign = (state: { experimentDesign: ExperimentDesignState }, designId: number) =>
    state.experimentDesign.queuedExperimentsCounts[designId] || 0;

// Export reducer
export default experimentDesignSlice.reducer; 